import React from "react";
import { useShallow } from "zustand/react/shallow";
import { Card as CardType } from "@uno/protocol";
import useStoreRoom from "store/useStoreRoom";
import useStoreRoomScene from "store/useStoreRoomScene";
import usePlayCard from "hook/room/usePlayCard";
import useClick from "hook/useClick";
import Card from "component/Card/Card";
import DiscardColorSelector from "./Entity/DiscardColorSelector";
import DiscardSwapPrompt from "./Entity/DiscardSwapPrompt";
import DiscardRotationIndicator from "./DiscardRotationIndicator";
import DiscardDrawComboIndicator from "./DiscardDrawComboIndicator";
import DiscardReconnectionOverlay from "./Entity/DiscardReconnectionOverlay";
import {
    TRANSFORM_STYLES
} from "types/Discard";
import {
    CARD_WIDTH, CARD_HEIGHT
} from "types/Card";
import { useRoomLayout } from "context/RoomLayoutContext";

const Discard: React.FC = () => {
    const room = useStoreRoom(useShallow(state => ({ discard: state.room?.game?.discard, discardOffset: state.room?.game?.discardOffset ?? 0, color: state.room?.game?.color })));
    const hoveredCardId = useStoreRoomScene(useShallow(state => state.hoveredCardId));
    const playCard = usePlayCard();
    const discardRef = React.useRef<HTMLDivElement>(null);
    const { scales, discardSize } = useRoomLayout();

    const handleDiscardClick = React.useCallback(() => {
        if(hoveredCardId) {
            playCard(hoveredCardId);
        }
    }, [hoveredCardId, playCard]);

    useClick(handleDiscardClick, discardRef);

    const discardStack = React.useMemo(() => {
        const discardCards = room?.discard || [];
        const offset = room?.discardOffset ?? 0;
        const stack: Record<number, CardType> = {};

        for(let i = 0; i < discardCards.length; i++) {
            stack[offset + i] = discardCards[i];
        }

        return stack;
    }, [room?.discard, room?.discardOffset]);

    const discardCards = React.useMemo(() => {
        return Object.entries(discardStack).map(([index, card]) => {
            const positionInDiscardStack = parseInt(index);
            const styleIndex = positionInDiscardStack % Object.keys(TRANSFORM_STYLES).length;
            const { rotation } = TRANSFORM_STYLES[styleIndex as keyof typeof TRANSFORM_STYLES];

            return (
                <div
                    key={card.id}
                    style={{
                        transform: `rotate(${rotation}deg)`,
                        position: "absolute",
                    }}
                >
                    <Card
                        id={card.id}
                        type={card.type}
                        positionInDiscardStack={positionInDiscardStack}
                        isHoverable={false}
                    />
                </div>
            );
        });
    }, [discardStack]);

    const color = room?.color?.toLowerCase() || "none";
    const colorStyle = {
        backgroundColor: `var(--${color}-color, #333)`,
        borderColor: `var(--${color}-border-color, #333)`,
    };

    return (
        <div
            ref={discardRef}
            id="discard"
            className="absolute w-40 h-40 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-full border-4"
            style={{
                ...colorStyle,
                width: `${discardSize}px`,
                height: `${discardSize}px`,
            }}
        >
            <DiscardReconnectionOverlay />
            <DiscardColorSelector />
            <DiscardSwapPrompt />
            <DiscardRotationIndicator ref={discardRef} />
            <DiscardDrawComboIndicator />

            <div id="discard-container" className="relative w-full h-full flex items-center justify-center rounded-full" style={{ transform: `scale(${scales.discard})` }}>
                <div
                    id="card-placeholder"
                    className="invisible absolute"
                    style={{
                        width: `${CARD_WIDTH}px`,
                        height: `${CARD_HEIGHT}px`,
                    }}
                ></div>
                
                {discardCards}
            </div>
        </div>
    );
};

export default React.memo(Discard);
